import { useRef, useState, useEffect } from "react";
import { preload } from "react-dom";
import { Layout } from "@/components/Layout";
import { LumaWordmark } from "@/components/LumaLogo";
import { Reveal } from "@/components/Reveal";
import { useScrollLight, LightRays } from "@/components/ScrollLight";
import { useListEvents } from "@workspace/api-client-react";
import { Link } from "wouter";
import { m, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { trackClick } from "@/lib/tracking";
import { useSiteContent } from "@/lib/content";
import { Seo } from "@/components/Seo";
import { ArrowRight, Play, Volume2, VolumeX } from "lucide-react";
import heroLights640 from "@/assets/hero-lights-640.webp";
import heroLights1024 from "@/assets/hero-lights-1024.webp";
import heroLights1600 from "@/assets/hero-lights-1600.webp";
import dancefloor640 from "@/assets/dancefloor-640.webp";
import dancefloor1024 from "@/assets/dancefloor-1024.webp";
import dancefloor1600 from "@/assets/dancefloor-1600.webp";

const heroLightsSrcSet = `${heroLights640} 640w, ${heroLights1024} 1024w, ${heroLights1600} 1600w`;
const dancefloorSrcSet = `${dancefloor640} 640w, ${dancefloor1024} 1024w, ${dancefloor1600} 1600w`;

function getYouTubeId(url: string): string | null {
  if (!url) return null;
  const patterns = [
    /[?&]v=([^&#]+)/,
    /youtu\.be\/([^?&#]+)/,
    /youtube\.com\/embed\/([^?&#]+)/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) return url;
  return null;
}

type EventItem = {
  id: number;
  slug: string;
  title: string;
  subtitle?: string | null;
  teaser: string;
  youtubeUrl: string;
  thumbnailUrl?: string | null;
  displayMode?: string;
  eventDate?: string | null;
  isActive: boolean;
};

function EventPreviewCard({ event, index }: { event: EventItem; index: number }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const videoBoxRef = useRef<HTMLDivElement>(null);
  const [muted, setMuted] = useState(true);
  // The autoplay preview iframe is only mounted once the card scrolls near the
  // viewport, so the heavy YouTube player never competes with the initial
  // render / LCP on the home page.
  const [inView, setInView] = useState(false);
  const reduce = useReducedMotion();
  const cardCta = useSiteContent().text("home.events.cardCta");

  // "Still Image" mode set in the admin dashboard: show the uploaded photo,
  // never the autoplay video preview.
  const showStillImage = event.displayMode === "image" && !!event.thumbnailUrl;

  const vidId = getYouTubeId(event.youtubeUrl);
  const embedSrc = vidId
    ? `https://www.youtube-nocookie.com/embed/${vidId}?autoplay=1&mute=1&loop=1&playlist=${vidId}&start=0&end=10&controls=0&showinfo=0&rel=0&modestbranding=1&iv_load_policy=3&disablekb=1&enablejsapi=1`
    : null;

  useEffect(() => {
    const el = videoBoxRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const toggleMute = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    const func = muted ? "unMute" : "mute";
    win.postMessage(JSON.stringify({ event: "command", func, args: "" }), "*");
    setMuted(prev => !prev);
  };

  return (
    <Link href="/events">
      <m.div
        initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24 }}
        whileInView={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6, delay: reduce ? 0 : index * 0.08, ease: [0.22, 1, 0.36, 1] }}
        className="group cursor-pointer bg-card border border-white/8 rounded-2xl overflow-hidden hover:border-primary/40 transition-all duration-500 flex flex-col md:flex-row min-h-[300px] md:min-h-[340px]"
      >
        {/* Media preview — left 58% */}
        <div ref={videoBoxRef} data-testid="home-event-preview" className="relative w-full md:w-[58%] aspect-video md:aspect-auto overflow-hidden bg-black flex-shrink-0">
          {showStillImage ? (
            <>
              {/* Blurred copy fills the cell so the full photo shows uncropped. */}
              <img
                src={event.thumbnailUrl!}
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-60"
              />
              <img
                src={event.thumbnailUrl!}
                alt={event.title}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-contain"
              />
            </>
          ) : embedSrc ? (
            <>
              {/* Thumbnail stand-in shown until the card scrolls into view, then
                  the autoplay preview iframe is mounted on top of it. */}
              <img
                src={event.thumbnailUrl || `https://i.ytimg.com/vi/${vidId}/hqdefault.jpg`}
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
                className="absolute -top-[4%] -left-[4%] w-[108%] h-[108%] object-cover"
              />
              {inView && (
                <iframe
                  ref={iframeRef}
                  data-testid="home-event-preview-iframe"
                  src={embedSrc}
                  title={event.title}
                  allow="autoplay; encrypted-media"
                  allowFullScreen={false}
                  className="absolute -top-[4%] -left-[4%] w-[108%] h-[108%] pointer-events-none"
                  style={{ border: 0 }}
                />
              )}
              {/* Transparent overlay — catches clicks for card navigation */}
              <div className="absolute inset-0 z-10" />
              {/* Sound toggle — sits above overlay, stops propagation */}
              <button
                onClick={toggleMute}
                aria-label={muted ? "Unmute preview" : "Mute preview"}
                className="absolute bottom-4 left-4 z-30 w-9 h-9 rounded-full bg-black/60 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-black/80 hover:scale-110 transition-all duration-200"
              >
                {muted
                  ? <VolumeX className="w-4 h-4" />
                  : <Volume2 className="w-4 h-4" />}
              </button>
            </>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent flex items-center justify-center">
              <Play className="w-16 h-16 text-primary/40" />
            </div>
          )}
          {/* Right-side gradient blend into card */}
          <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-r from-transparent to-card z-20 pointer-events-none" />
        </div>

        {/* Text info — right 42% */}
        <div className="flex-1 flex flex-col justify-center px-8 md:px-10 py-8 md:py-10">
          {event.eventDate && (
            <p className="font-mono text-xs text-primary uppercase tracking-[0.3em] mb-3">
              {new Date(event.eventDate).toLocaleDateString("en-US", {
                year: "numeric", month: "long", day: "numeric",
              })}
            </p>
          )}
          <h3 className="font-heading text-3xl md:text-4xl xl:text-5xl text-foreground leading-tight mb-4 group-hover:text-primary transition-colors duration-300">
            {event.title}
          </h3>
          {event.subtitle && (
            <h2 className="font-heading text-lg md:text-xl text-secondary tracking-wide -mt-2 mb-4">
              {event.subtitle}
            </h2>
          )}
          <p className="text-muted-foreground font-sans text-sm md:text-base leading-relaxed line-clamp-3">
            {event.teaser}
          </p>
          <div className="mt-8 flex items-center gap-2 font-heading text-sm tracking-widest uppercase text-primary group-hover:gap-4 transition-all duration-300">
            {cardCta} <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </m.div>
    </Link>
  );
}

export default function Home() {
  const { data: eventsData, isLoading: isLoadingEvents } = useListEvents({
    showOn: "home",
    pageSize: 3,
  });
  const events = eventsData?.items ?? [];
  const content = useSiteContent();
  const marqueeText = content.list("home.marquee.phrases").join(" · ") + " · ";

  // Preload the hero background — the home LCP element — so the browser starts
  // fetching it during render, before the <img> mounts. imageSrcSet/imageSizes
  // mirror the <img> below exactly so the preload and the img resolve to the
  // same candidate (one fetch, no double download). preload() dedupes.
  preload(heroLights1600, {
    as: "image",
    fetchPriority: "high",
    imageSrcSet: heroLightsSrcSet,
    imageSizes: "100vw",
  });

  const reduce = useReducedMotion();

  // Scroll-linked lighting: each section "switches on" as it scrolls into view.
  const eventsRef = useRef<HTMLElement>(null);
  const brandRef = useRef<HTMLElement>(null);
  const eventsLight = useScrollLight(eventsRef);
  const brandLight = useScrollLight(brandRef);

  return (
    <Layout>
      <Seo
        title="LUMA Entertainment"
        description="LUMA Entertainment — a premium women-forward entertainment brand that creates, films, hosts, and promotes bold, magnetic events and content. Light It Up."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "LUMA Entertainment",
          alternateName: "MyLuma.TV",
          url: "https://myluma.tv",
          logo: "https://myluma.tv/favicon.svg",
          email: "hello@myluma.tv",
          description:
            "A premium women-forward entertainment brand that creates, films, hosts, and promotes bold, magnetic events and content.",
          slogan: "Light It Up",
        }}
      />
      {/* ── HERO ─────────────────────────────────────────────────── */}
      <section className="relative">
        <div className="hero-bg relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
          {/* Photographic atmosphere — stage lights (home LCP image) */}
          <img
            src={heroLights1600}
            srcSet={heroLightsSrcSet}
            sizes="100vw"
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            decoding="async"
            className="absolute -top-[12%] left-0 w-full h-[124%] object-cover opacity-40 pointer-events-none"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/50 to-background pointer-events-none" />
          {/* Bottom vignette fade — blends the hero seamlessly into the marquee/events flow */}
          <div className="absolute bottom-0 left-0 right-0 h-56 bg-gradient-to-t from-background via-background/80 to-transparent pointer-events-none z-10" />

          <div className="relative z-10 px-4">
            <m.div
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: "easeOut" }}
              className="text-center flex flex-col items-center"
            >
              {/* Full logo — SVG wordmark (LUMA letterforms + ENTERTAINMENT) */}
              <h1 className="sr-only">LUMA Entertainment</h1>
              <LumaWordmark className="w-full max-w-sm md:max-w-xl lg:max-w-2xl xl:max-w-3xl h-auto mb-6" />

              {/* Tagline — supporting, not the hero */}
              <p className="font-heading text-secondary tracking-[0.45em] uppercase text-sm md:text-base mb-4" data-testid="hero-tagline">
                {content.text("home.hero.tagline")}
              </p>
              <p className="text-secondary/70 font-serif italic text-base md:text-xl mb-12 tracking-wide">
                {content.text("home.hero.subtitle")}
              </p>

              <div className="flex gap-4 flex-wrap justify-center">
                <Link href="/events">
                  <Button
                    size="lg"
                    onClick={() =>
                      trackClick({
                        targetType: "cta",
                        targetSlug: "hero-view-events",
                        targetName: "View Events",
                        pagePath: "/",
                      })
                    }
                    className="h-14 px-10 bg-primary hover:bg-primary/90 text-primary-foreground font-heading text-lg tracking-widest uppercase"
                  >
                    {content.text("home.hero.ctaPrimary")}
                  </Button>
                </Link>
                <Link href="/about">
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() =>
                      trackClick({
                        targetType: "cta",
                        targetSlug: "hero-our-story",
                        targetName: "Our Story",
                        pagePath: "/",
                      })
                    }
                    className="h-14 px-10 border-secondary/60 text-secondary hover:bg-secondary/10 font-heading text-lg tracking-widest uppercase"
                  >
                    {content.text("home.hero.ctaSecondary")}
                  </Button>
                </Link>
              </div>
            </m.div>
          </div>
        </div>
      </section>

      {/* ── MARQUEE — transition band bridging hero into events ──── */}
      <div className="relative z-20 -mt-px overflow-hidden border-y border-primary/20 bg-background py-3">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...Array(4)].map((_, i) => (
            <span key={i} className="text-primary font-heading tracking-[0.3em] text-sm">
              {marqueeText}
            </span>
          ))}
        </div>
      </div>

      {/* ── EVENTS ───────────────────────────────────────────────── */}
      <section ref={eventsRef} id="events" className="relative overflow-hidden bg-background py-24">
        {/* Stage-light beams bloom in as the section scrolls into view */}
        <LightRays opacity={eventsLight.raysOpacity} scale={eventsLight.raysScale} reduce={reduce} />

        {/* Section header */}
        <Reveal className="relative z-10 px-6 md:px-12 mb-12 flex items-end justify-between">
          <div>
            <m.h2
              style={{ filter: eventsLight.headingFilter }}
              className="font-heading text-7xl md:text-9xl text-foreground leading-none tracking-tight"
            >
              {content.text("home.events.heading")}
            </m.h2>
            <p className="font-heading text-xs tracking-[0.4em] text-secondary mt-2">{content.text("home.events.eyebrow")}</p>
          </div>
          <Link href="/events">
            <button className="hidden md:flex items-center gap-2 font-heading text-sm tracking-widest uppercase text-primary hover:text-primary/70 transition-colors group mb-2">
              {content.text("home.events.seeAll")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </Link>
        </Reveal>

        {/* Events list */}
        {isLoadingEvents ? (
          <div className="relative z-10 px-6 md:px-12 space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="h-72 bg-card animate-pulse rounded-2xl" />
            ))}
          </div>
        ) : events.length === 0 ? (
          <p className="relative z-10 text-muted-foreground font-serif italic text-center py-16 text-xl px-6">
            {content.text("home.events.empty")}
          </p>
        ) : (
          <div className="relative z-10 px-4 md:px-8 flex flex-col gap-16 md:gap-24">
            {events.slice(0, 3).map((event, i) => (
              <EventPreviewCard key={event.id} event={event} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* ── BRAND STATEMENT ──────────────────────────────────────── */}
      <section ref={brandRef} className="hero-bg py-32 text-center relative overflow-hidden">
        <img
          src={dancefloor1600}
          srcSet={dancefloorSrcSet}
          sizes="100vw"
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/60 to-background pointer-events-none" />
        {/* Stage-light beams bloom in as the section scrolls into view */}
        <LightRays opacity={brandLight.raysOpacity} scale={brandLight.raysScale} reduce={reduce} />
        <div className="container mx-auto px-6 relative z-10">
          <Reveal duration={0.8}>
            <p className="font-heading text-xs tracking-[0.5em] text-secondary mb-6">{content.text("home.brand.eyebrow")}</p>
            <m.h2
              style={{ filter: brandLight.headingFilter }}
              className="font-heading text-5xl md:text-8xl lg:text-9xl text-foreground leading-tight mb-6"
            >
              {content.text("home.brand.headingLine1")}<br />
              <span className="text-primary">{content.text("home.brand.headingLine2")}</span>
            </m.h2>
            <p className="font-serif italic text-secondary/70 text-xl mb-12">
              {content.text("home.brand.subtitle")}
            </p>
            <Link href="/about">
              <Button
                onClick={() =>
                  trackClick({
                    targetType: "cta",
                    targetSlug: "brand-our-story",
                    targetName: "Our Story",
                    pagePath: "/",
                  })
                }
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading tracking-widest uppercase h-14 px-12 text-lg"
              >
                {content.text("home.brand.cta")}
              </Button>
            </Link>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}
